import { useEffect, useRef, useState } from 'react';

/**
 * A select we can actually style.
 *
 * The native control's menu is drawn by the operating system and no
 * page can touch it, so matching the rest of the interface means
 * replacing it rather than restyling it. That is a real cost: this
 * has to carry the keyboard and focus behaviour the native one gave
 * away for free, which is why it is used only where the menu is
 * visible enough to matter.
 */
export type Choice = { value: string; label: string };

export const Picker = ({ label, value, options, onChange }: {
  label?: string;
  value: string;
  options: Choice[];
  onChange: (v: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const box = useRef<HTMLDivElement>(null);

  const current = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    setCursor(Math.max(0, options.findIndex((o) => o.value === value)));

    const away = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open, options, value]);

  const pick = (v: string) => {
    onChange(v);
    setOpen(false);
  };

  /** The keys a native select answers to, since we took it away. */
  const keys = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') return setOpen(false);

    if (!open && (e.key === 'Enter' || e.key === ' '
      || e.key === 'ArrowDown')) {
      e.preventDefault();
      return setOpen(true);
    }

    if (!open) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const choice = options[cursor];
      if (choice) pick(choice.value);
    }
  };

  return (
    <div className="picker" ref={box}>
      {label && <span className="mono pickerLabel">{label}</span>}
      <button
        type="button"
        className={open ? 'pickerBtn on' : 'pickerBtn'}
        onClick={() => setOpen(!open)}
        onKeyDown={keys}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>{current?.label ?? ''}</span>
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none"
             stroke="currentColor" strokeWidth="2"
             strokeLinecap="round" aria-hidden="true">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <ul className="pickerMenu" role="listbox" tabIndex={-1}>
          {options.map((o, i) => (
            <li key={o.value}>
              <button
                type="button"
                role="option"
                aria-selected={o.value === value}
                className={[
                  'pickerItem',
                  o.value === value ? 'on' : '',
                  i === cursor ? 'cursor' : '',
                ].filter(Boolean).join(' ')}
                onMouseEnter={() => setCursor(i)}
                onClick={() => pick(o.value)}
              >
                {o.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
